import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./CommunitySettingsModal.module.css";
import {
  fetchAllCollections,
  fetchAllCollectionProjects,
  fetchAllSchemas,
  fetchProjectsByCommunity,
  fetchAllCommunityEntitiesAdmin,
  fetchAllCollectionMembershipsSec,
  fetchAllSecurityUsers,
  createCollection,
  updateCollection,
  addProjectToCollection,
  removeProjectFromCollection,
  upsertCollectionMembership,
  deleteCollectionMembership,
  // DEV-877: filter the project picker to projects whose primary_owner_org
  // (or active partner-org-walked-root) matches the selected collection's
  // owner-scope walked to org root.
  fetchEligibleProjectsForCollection,
  // DEV-1096: polymorphic Mini-Project membership in collections.
  addMiniProjectToCollection,
  removeMiniProjectFromCollection,
  fetchEligibleMiniProjectsForCollection,
} from "../../api";
import type {
  CollectionResponse,
  CollectionProjectResponse,
  CollectionSchemaResponse,
  ProjectResponse,
  CommunityEntityResponse,
  CollectionMembershipSecResponse,
  SecurityUserResponse,
  EligibleMiniProjectResponse,
} from "../../api";
import { miniLifecycleToProjectStatus } from "../../Data/MiniProjectConfig/miniProjectLifecycle";

interface Props {
  communityGuid?: string;
}

type EditingCollection = {
  collectionGuid: string | null; // null = new
  collectionName: string;
  schemaGuid: string;
  description: string;
  ownerOrg: string;
  isActive: boolean;
};

type ConfirmAction = {
  label: string;
  detail: string;
  onConfirm: () => void;
};

/* ── Sub-tab type ── */
type SubTab = "collections" | "projects";

const CommunityCollectionsTab: React.FC<Props> = ({ communityGuid }) => {
  const { t } = useAppTranslation('community');
  const [collections, setCollections] = useState<CollectionResponse[]>([]);
  const [collectionProjects, setCollectionProjects] = useState<CollectionProjectResponse[]>([]);
  const [schemas, setSchemas] = useState<CollectionSchemaResponse[]>([]);
  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [communityEntities, setCommunityEntities] = useState<CommunityEntityResponse[]>([]);
  const [collectionMemberships, setCollectionMemberships] = useState<CollectionMembershipSecResponse[]>([]);
  const [securityUsers, setSecurityUsers] = useState<SecurityUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Permissions panel
  const [permissionsExpandedGuid, setPermissionsExpandedGuid] = useState<string | null>(null);
  const [savingMemberKey, setSavingMemberKey] = useState<string | null>(null); // "collGuid|userGuid"
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedUserGuid, setSelectedUserGuid] = useState("");
  const [selectedMemberRole, setSelectedMemberRole] = useState<string>("member_viewer");

  // Sub-tab
  const [subTab, setSubTab] = useState<SubTab>("collections");

  // DEV-877: eligible-project guids for the currently-selected collection.
  // null = not loaded yet (no collection selected, or in flight). When set,
  // the project picker filters its list to include both already-attached
  // projects (so users can detach them) AND eligible-but-unattached
  // candidates. The eligibility endpoint excludes already-attached by
  // default; we union those with the existing collection_projects rows.
  const [eligibleGuids, setEligibleGuids] = useState<Set<string> | null>(null);
  const [eligibleLoading, setEligibleLoading] = useState(false);

  // Editing state (collections sub-tab)
  const [editing, setEditing] = useState<EditingCollection | null>(null);
  const [expandedGuid, setExpandedGuid] = useState<string | null>(null);

  // Feedback
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  // Search / filter (collections sub-tab)
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  // Search (projects sub-tab)
  const [projSearch, setProjSearch] = useState("");

  // Owner/Org typeahead
  const [ownerOrgQuery, setOwnerOrgQuery] = useState("");
  const [showOwnerSuggestions, setShowOwnerSuggestions] = useState(false);
  const [ownerTypeFilter, setOwnerTypeFilter] = useState<"all" | "geo" | "org">("all");
  const ownerOrgRef = React.useRef<HTMLDivElement>(null);

  // Selected collection for the Assign Projects checklist
  const [assignCollGuid, setAssignCollGuid] = useState<string>("");

  // Track in-flight toggle per project (projects sub-tab)
  const [savingProjectId, setSavingProjectId] = useState<string | null>(null);
  // DEV-1096. Picker mode in the Assign sub-tab. Skip's lock: toggle UI
  // between Full-Project and Mini-Project membership, not a single picker.
  const [pickerMode, setPickerMode] = useState<"projects" | "mini-projects">("projects");
  // In-flight toggle key for the MP picker — distinct from savingProjectId
  // so a click on either side doesn't blank the other side's spinner.
  const [savingMiniProjectId, setSavingMiniProjectId] = useState<string | null>(null);

  // Auto-dismiss success message
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 3500);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Close owner suggestions on outside click
  useEffect(() => {
    if (!showOwnerSuggestions) return;
    const onDocClick = (e: MouseEvent) => {
      if (ownerOrgRef.current && e.target instanceof Node && !ownerOrgRef.current.contains(e.target)) {
        setShowOwnerSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showOwnerSuggestions]);

  /* ── Data loading ── */

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetches: Promise<any>[] = [
        fetchAllCollections(communityGuid).catch(() => [] as CollectionResponse[]),
        fetchAllCollectionProjects().catch(() => [] as CollectionProjectResponse[]),
        fetchAllSchemas().catch(() => [] as CollectionSchemaResponse[]),
        fetchAllCommunityEntitiesAdmin().catch(() => [] as CommunityEntityResponse[]),
        fetchAllCollectionMembershipsSec().catch(() => [] as CollectionMembershipSecResponse[]),
        fetchAllSecurityUsers().catch(() => [] as SecurityUserResponse[]),
      ];
      if (communityGuid) {
        fetches.push(fetchProjectsByCommunity(communityGuid).catch(() => [] as ProjectResponse[]));
      }
      const [colls, cps, schs, entities, memberships, users, projs] = await Promise.all(fetches);
      setCollections(colls);
      setCollectionProjects(cps);
      setSchemas(schs);
      setCommunityEntities(entities);
      setCollectionMemberships(memberships);
      setSecurityUsers(users);
      if (projs) setProjects(projs);
      if (!colls.length && !schs.length) {
        setError("API unavailable — ensure the API is running.");
      }
    } finally {
      setLoading(false);
    }
  }, [communityGuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ── Derived data (declared before effects that depend on it) ── */

  // DEV-1096. Split the polymorphic collection_projects rows by member_type
  // so the existing Full-Project surfaces (linkedSet, project-name displays,
  // unlink buttons) operate only on rows that actually carry a project_guid.
  // Mini-Project rows are surfaced via their own state below.
  // NOTE: defined above the eligibility effects because both effects depend
  // on this memo through their deps array; React useEffect runs after
  // render, but the deps array is read synchronously and TDZ-violates if
  // the memo isn't declared yet (initial bug fix on this file).
  const projectMembershipRows = useMemo(
    () => collectionProjects.filter((cp) =>
      (cp.memberType ?? "project") === "project" && !!cp.projectGuid,
    ),
    [collectionProjects],
  );

  // DEV-877: load eligible-project guids whenever the selected collection
  // changes. Reuses the eligibility endpoint to scope the picker. Already-
  // attached project guids are folded in via the existing collectionProjects
  // state so the user can still uncheck them.
  useEffect(() => {
    if (!assignCollGuid) {
      setEligibleGuids(null);
      return;
    }
    let cancelled = false;
    setEligibleLoading(true);
    fetchEligibleProjectsForCollection(assignCollGuid)
      .then((eligible) => {
        if (cancelled) return;
        const set = new Set<string>(eligible.map((p) => p.id));
        // Fold in currently-attached projects so the user can detach them
        // even if they would no longer pass the eligibility predicate (e.g.
        // legacy attachments from before DEV-877 shipped).
        // DEV-1096: only fold in Full-Project rows — MP rows have null
        // project_guid and would poison this set with an invalid id.
        for (const cp of projectMembershipRows) {
          if (cp.collectionGuid === assignCollGuid && cp.projectGuid) {
            set.add(cp.projectGuid);
          }
        }
        setEligibleGuids(set);
      })
      .catch(() => {
        if (cancelled) return;
        // On error, fall back to "no filter" so the picker keeps working
        // rather than going empty. Server still enforces the gate.
        setEligibleGuids(null);
      })
      .finally(() => {
        if (!cancelled) setEligibleLoading(false);
      });
    return () => { cancelled = true; };
  }, [assignCollGuid, projectMembershipRows]);

  // DEV-1096. Eligible Mini-Projects for the selected collection's picker.
  // Visibility/membership scope is applied server-side (Skip's lock:
  // visibility in {public,community} OR caller is on team / role roster).
  // Mirrors the project-side eligibility effect.
  const [eligibleMiniProjects, setEligibleMiniProjects] =
    useState<EligibleMiniProjectResponse[] | null>(null);
  const [eligibleMpLoading, setEligibleMpLoading] = useState(false);

  useEffect(() => {
    if (!assignCollGuid) {
      setEligibleMiniProjects(null);
      return;
    }
    let cancelled = false;
    setEligibleMpLoading(true);
    // includeAttached=true so the user can see — and uncheck — currently-
    // attached MPs from the same list. Mirrors the project flow's behavior
    // post-DEV-877 (we fold attached into the eligibility set above).
    fetchEligibleMiniProjectsForCollection(assignCollGuid, { includeAttached: true })
      .then((rows) => {
        if (cancelled) return;
        setEligibleMiniProjects(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setEligibleMiniProjects([]);
      })
      .finally(() => {
        if (!cancelled) setEligibleMpLoading(false);
      });
    return () => { cancelled = true; };
  }, [assignCollGuid]);

  /* ── Remaining derived data ── */

  const miniProjectMembershipRows = useMemo(
    () => collectionProjects.filter((cp) =>
      cp.memberType === "mini_project" && !!cp.miniProjectGuid,
    ),
    [collectionProjects],
  );

  // Per-collection counts include BOTH member types — collection cards
  // show total members. The picker UI below splits them per-tab.
  const projectCountByCollection = useMemo(() => {
    const map: Record<string, number> = {};
    for (const cp of collectionProjects) {
      map[cp.collectionGuid] = (map[cp.collectionGuid] || 0) + 1;
    }
    return map;
  }, [collectionProjects]);

  /** Set of "collectionGuid|projectGuid" keys for O(1) Full-Project membership check */
  const linkedSet = useMemo(() => {
    const s = new Set<string>();
    for (const cp of projectMembershipRows) {
      s.add(`${cp.collectionGuid}|${cp.projectGuid}`);
    }
    return s;
  }, [projectMembershipRows]);

  const isLinked = useCallback(
    (collectionGuid: string, projectGuid: string) =>
      linkedSet.has(`${collectionGuid}|${projectGuid}`),
    [linkedSet]
  );

  /** DEV-1096. Same shape for Mini-Project membership. */
  const linkedMpSet = useMemo(() => {
    const s = new Set<string>();
    for (const cp of miniProjectMembershipRows) {
      s.add(`${cp.collectionGuid}|${cp.miniProjectGuid}`);
    }
    return s;
  }, [miniProjectMembershipRows]);

  const isMpLinked = useCallback(
    (collectionGuid: string, miniProjectGuid: string) =>
      linkedMpSet.has(`${collectionGuid}|${miniProjectGuid}`),
    [linkedMpSet]
  );

  const activeCollections = useMemo(
    () => collections.filter((c) => c.isActive),
    [collections]
  );

  const filteredCollections = useMemo(() => {
    let result = collections;
    if (statusFilter === "active") result = result.filter((c) => c.isActive);
    if (statusFilter === "inactive") result = result.filter((c) => !c.isActive);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.collectionName.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.ownerOrg.toLowerCase().includes(q) ||
          c.collectionGuid.toLowerCase().includes(q)
      );
    }
    return result;
  }, [collections, statusFilter, searchTerm]);

  const filteredProjects = useMemo(() => {
    // DEV-877: when a collection is selected and we have an eligibility set,
    // restrict the picker to eligible projects (which the effect above
    // already unions with currently-attached). When no collection is
    // selected, the picker isn't visible — but we still return the broad
    // list so the search count summary is meaningful.
    let result = projects;
    if (assignCollGuid && eligibleGuids) {
      result = result.filter((p) => eligibleGuids.has(p.id));
    }
    if (!projSearch.trim()) return result;
    const q = projSearch.toLowerCase().trim();
    return result.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.id || "").toLowerCase().includes(q) ||
        (p.projectStatus || "").toLowerCase().includes(q) ||
        // DEV-1271: mini-projects search by their lifecycle-derived status.
        miniLifecycleToProjectStatus(p.status).toLowerCase().includes(q)
    );
  }, [projects, projSearch, assignCollGuid, eligibleGuids]);

  /** Organization suggestions for the Owner/Org typeahead */
  const ownerOrgSuggestions = useMemo(() => {
    const q = (ownerOrgQuery ?? "").trim().toLowerCase();
    if (!q) return [] as CommunityEntityResponse[];
    const scored: { e: CommunityEntityResponse; score: number }[] = [];
    for (const e of communityEntities) {
      if (!e.isActive) continue;
      // Type filter
      if (ownerTypeFilter === "geo" && !e.entityType.startsWith("geo_")) continue;
      if (ownerTypeFilter === "org" && e.entityType.startsWith("geo_")) continue;
      const name = (e.name || "").toLowerCase();
      const code = (e.code || "").toLowerCase();
      const type = (e.entityType || "").toLowerCase();
      let score = -1;
      if (name.startsWith(q)) score = 100;
      else if (code.startsWith(q)) score = 90;
      else if (name.includes(q)) score = 70;
      else if (code.includes(q)) score = 60;
      else if (type.includes(q)) score = 40;
      if (score < 0) continue;
      scored.push({ e, score });
    }
    scored.sort((a, b) => b.score - a.score || a.e.name.localeCompare(b.e.name));
    return scored.slice(0, 12).map((x) => x.e);
  }, [ownerOrgQuery, communityEntities, ownerTypeFilter]);

  /* ── Collection CRUD helpers ── */

  const startCreate = () => {
    setEditing({
      collectionGuid: null,
      collectionName: "",
      schemaGuid: schemas[0]?.schemaGuid || "",
      description: "",
      ownerOrg: "",
      isActive: true,
    });
    setOwnerOrgQuery("");
    setShowOwnerSuggestions(false);
    setError(null);
  };

  const startEdit = (c: CollectionResponse) => {
    setEditing({
      collectionGuid: c.collectionGuid,
      collectionName: c.collectionName,
      schemaGuid: c.schemaGuid,
      description: c.description,
      ownerOrg: c.ownerOrg,
      isActive: c.isActive,
    });
    setOwnerOrgQuery(c.ownerOrg || "");
    setShowOwnerSuggestions(false);
    setError(null);
  };

  const cancelEdit = () => setEditing(null);

  const saveCollection = async () => {
    if (!editing) return;
    if (!editing.collectionName.trim()) {
      setError("Collection name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing.collectionGuid) {
        await updateCollection(editing.collectionGuid, {
          collectionName: editing.collectionName.trim(),
          schemaGuid: editing.schemaGuid || undefined,
          description: editing.description.trim(),
          ownerOrg: editing.ownerOrg.trim(),
          isActive: editing.isActive,
        });
        setSuccessMsg(`"${editing.collectionName.trim()}" updated.`);
      } else {
        await createCollection({
          collectionName: editing.collectionName.trim(),
          schemaGuid: editing.schemaGuid || undefined,
          communityGuid: communityGuid || undefined,
          description: editing.description.trim(),
          ownerOrg: editing.ownerOrg.trim(),
        });
        setSuccessMsg(`"${editing.collectionName.trim()}" created.`);
      }
      setEditing(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to save collection.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: CollectionResponse) => {
    setSaving(true);
    setError(null);
    setConfirmAction(null);
    try {
      await updateCollection(c.collectionGuid, { isActive: !c.isActive });
      setSuccessMsg(`"${c.collectionName}" ${c.isActive ? "deactivated" : "activated"}.`);
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to update collection.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProject = async (collectionGuid: string, projectGuid: string) => {
    setSaving(true);
    setError(null);
    setConfirmAction(null);
    try {
      await removeProjectFromCollection(collectionGuid, projectGuid);
      setSuccessMsg("Project unlinked from collection.");
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to remove project from collection.");
    } finally {
      setSaving(false);
    }
  };

  /** DEV-1096. Toggle a Mini-Project's membership in the currently-selected
   *  collection. Mirrors toggleProjectInCollection — server enforces both
   *  eligibility and visibility; UI surfaces the friendly server message on
   *  rejection. */
  const toggleMiniProjectInCollection = async (miniProjectGuid: string) => {
    if (!assignCollGuid) return;
    setSavingMiniProjectId(miniProjectGuid);
    setError(null);
    try {
      if (isMpLinked(assignCollGuid, miniProjectGuid)) {
        await removeMiniProjectFromCollection(assignCollGuid, miniProjectGuid);
        setSuccessMsg("Mini-Project removed from collection.");
      } else {
        await addMiniProjectToCollection(assignCollGuid, miniProjectGuid);
        setSuccessMsg("Mini-Project added to collection.");
      }
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to update Mini-Project membership.");
    } finally {
      setSavingMiniProjectId(null);
    }
  };

  /** Toggle a project's membership in the currently-selected collection */
  const toggleProjectInCollection = async (projectGuid: string) => {
    if (!assignCollGuid) return;
    setSavingProjectId(projectGuid);
    setError(null);
    try {
      if (isLinked(assignCollGuid, projectGuid)) {
        await removeProjectFromCollection(assignCollGuid, projectGuid);
        setSuccessMsg("Project removed from collection.");
      } else {
        // DEV-877: server enforces eligibility. The picker should already
        // hide ineligible projects (eligibleGuids), but a stale UI or a
        // race between fetch and click can still let an ineligible add
        // through. The server response is the source of truth — surface
        // the friendly message verbatim if the gate rejects.
        await addProjectToCollection(assignCollGuid, projectGuid);
        setSuccessMsg("Project added to collection.");
      }
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to update project membership.");
    } finally {
      setSavingProjectId(null);
    }
  };

  // DEV-1096: the expanded per-collection list currently renders Full-Project
  // names + unlink buttons. Scope this list to project rows (MP rows are
  // surfaced separately) so name lookup doesn't fall through to "(unknown)".
  const projectsForCollection = useCallback(
    (collectionGuid: string) =>
      projectMembershipRows.filter((cp) => cp.collectionGuid === collectionGuid),
    [projectMembershipRows]
  );

  const schemaName = useCallback(
    (schemaGuid: string) =>
      schemas.find((s) => s.schemaGuid === schemaGuid)?.schemaName || schemaGuid || "(none)",
    [schemas]
  );

  /** Resolve a project GUID to its name (for expanded lists) */
  const projectName = useCallback(
    (projectGuid: string) => {
      const p = projects.find((pr) => pr.id === projectGuid);
      return p ? p.name : projectGuid;
    },
    [projects]
  );

  /** Get memberships for a given collection */
  const membershipsForCollection = useCallback(
    (collectionGuid: string) =>
      collectionMemberships.filter((m) => m.collectionGuid === collectionGuid),
    [collectionMemberships]
  );

  /** Resolve a user GUID to display name */
  const userName = useCallback(
    (userGuid: string) => {
      const u = securityUsers.find((su) => su.userGuid === userGuid);
      return u ? u.displayName : userGuid;
    },
    [securityUsers]
  );

  /** User search results for membership typeahead */
  const memberSearchResults = useMemo(() => {
    if (!memberSearch.trim() || !permissionsExpandedGuid) return [];
    const q = memberSearch.toLowerCase().trim();
    const existingGuids = new Set(
      membershipsForCollection(permissionsExpandedGuid).map((m) => m.userGuid)
    );
    return securityUsers
      .filter(
        (u) =>
          u.isActive &&
          !existingGuids.has(u.userGuid) &&
          (u.displayName.toLowerCase().includes(q) ||
            u.userGuid.toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [memberSearch, permissionsExpandedGuid, securityUsers, membershipsForCollection]);

  /** Add or update a collection membership */
  const handleAddMember = async (collectionGuid: string, userGuid: string, role: string) => {
    const key = `${collectionGuid}|${userGuid}`;
    setSavingMemberKey(key);
    setError(null);
    try {
      await upsertCollectionMembership({ collectionGuid, userGuid, role });
      setSuccessMsg(`User added as ${roleLabel(role)}.`);
      setMemberSearch("");
      setSelectedUserGuid("");
      setSelectedMemberRole("member_viewer");
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to add member.");
    } finally {
      setSavingMemberKey(null);
    }
  };

  /** Update role for existing membership */
  const handleRoleChange = async (collectionGuid: string, userGuid: string, newRole: string) => {
    const key = `${collectionGuid}|${userGuid}`;
    setSavingMemberKey(key);
    setError(null);
    try {
      await upsertCollectionMembership({ collectionGuid, userGuid, role: newRole });
      setSuccessMsg(`Role updated to ${roleLabel(newRole)}.`);
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to update role.");
    } finally {
      setSavingMemberKey(null);
    }
  };

  /** Remove a collection membership */
  const handleRemoveMember = async (membershipId: string, displayName: string) => {
    setSaving(true);
    setError(null);
    setConfirmAction(null);
    try {
      await deleteCollectionMembership(membershipId);
      setSuccessMsg(`${displayName} removed from collection.`);
      await loadData();
    } catch (err: any) {
      setError(err.message || "Failed to remove member.");
    } finally {
      setSaving(false);
    }
  };

  const confirmRemoveMember = (membership: CollectionMembershipSecResponse, collectionName: string) => {
    const name = userName(membership.userGuid);
    setConfirmAction({
      label: "Remove Member",
      detail: `Remove "${name}" from "${collectionName}"? They will lose collection-derived access to its projects.`,
      onConfirm: () => handleRemoveMember(membership.id, name),
    });
  };

  /** Human-readable role label (localized).
   *  DEV-1119 vocabulary sweep — the community ns key block was renamed
   *  from `roles.*` to `permissionTiers.*` to match POLICY_ROLES_AND_TEAMS
   *  §2: these labels are V2 PermissionSet tiers, not slot-bearing roles. */
  const roleLabel = (role: string): string =>
    t(`permissionTiers.${role}`, { defaultValue: role });

  /* ── Confirmation dialog helpers ── */

  const confirmDeactivate = (c: CollectionResponse) => {
    setConfirmAction({
      label: c.isActive ? "Deactivate Collection" : "Activate Collection",
      detail: c.isActive
        ? `Deactivate "${c.collectionName}"? Projects will remain linked but the collection won't appear in active lists.`
        : `Activate "${c.collectionName}"? It will become visible in active collection lists.`,
      onConfirm: () => toggleActive(c),
    });
  };

  const confirmUnlink = (collectionGuid: string, projectGuid: string, collectionName: string) => {
    const name = projectName(projectGuid);
    setConfirmAction({
      label: "Unlink Project",
      detail: `Remove "${name}" from "${collectionName}"? This does not delete the project.`,
      onConfirm: () => handleRemoveProject(collectionGuid, projectGuid),
    });
  };

  /* ── Render ── */

  if (loading) {
    return (
      <div className={styles.section}>
        <div className={styles.placeholder}>{t('collections.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.sectionWide}>
      {/* ── Success toast ── */}
      {successMsg ? (
        <div
          style={{
            borderRadius: 12,
            border: "1px solid rgba(34,197,94,0.35)",
            background: "rgba(34,197,94,0.10)",
            color: "rgba(240,253,244,0.95)",
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 16 }}>{"\u2705"}</span>
          {successMsg}
        </div>
      ) : null}

      {/* ── Error banner ── */}
      {error ? (
        <div
          className={styles.notice}
          style={{
            borderColor: "rgba(239,68,68,0.35)",
            background: "rgba(239,68,68,0.08)",
            color: "rgba(254,226,226,0.95)",
          }}
        >
          {error}
        </div>
      ) : null}

      {/* ── Confirmation dialog ── */}
      {confirmAction ? (
        <div
          style={{
            borderRadius: 12,
            border: "1px solid rgba(251,191,36,0.45)",
            background: "rgba(251,191,36,0.08)",
            color: "rgba(254,243,199,0.95)",
            padding: "12px 14px",
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{confirmAction.label}</div>
          <div style={{ marginBottom: 10, lineHeight: 1.4 }}>{confirmAction.detail}</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className={styles.btnMini} type="button" onClick={() => setConfirmAction(null)} disabled={saving}>
              Cancel
            </button>
            <button
              className={styles.btnMini}
              type="button"
              style={{ borderColor: "rgba(251,191,36,0.55)", background: "rgba(251,191,36,0.15)", color: "rgba(254,243,199,0.95)" }}
              onClick={confirmAction.onConfirm}
              disabled={saving}
            >
              {saving ? "Working..." : "Confirm"}
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Sub-tab selector ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button
          type="button"
          className={subTab === "collections" ? styles.tabActive : styles.tab}
          onClick={() => setSubTab("collections")}
        >
          Manage Collections
        </button>
        <button
          type="button"
          className={subTab === "projects" ? styles.tabActive : styles.tab}
          onClick={() => setSubTab("projects")}
        >
          Assign Projects
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── COLLECTIONS SUB-TAB ──────────────────────────────────── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {subTab === "collections" && (
        <>
          {/* Toolbar */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <div className={styles.sectionTitle} style={{ marginBottom: 0, flexShrink: 0 }}>
              Collections ({filteredCollections.length}
              {filteredCollections.length !== collections.length ? ` of ${collections.length}` : ""})
            </div>
            <div style={{ flex: 1 }} />
            <input
              className={styles.input}
              style={{ width: 200, padding: "6px 10px", fontSize: 12 }}
              placeholder={t('collections.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className={styles.select}
              style={{ width: 110, padding: "6px 10px", fontSize: 12 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
            >
              <option value="all">{t('collections.allStatus')}</option>
              <option value="active">{t('collections.active')}</option>
              <option value="inactive">{t('collections.inactive')}</option>
            </select>
            <button className={styles.btnPrimary} type="button" onClick={startCreate} disabled={saving || !!editing} style={{ flexShrink: 0 }}>
              + {t('common.new')} {t('common.collection')}
            </button>
          </div>

          {/* Create/Edit form */}
          {editing ? (
            <div className={styles.memberTableWrap} style={{ marginBottom: 14, padding: 16 }}>
              <div className={styles.sectionTitle} style={{ marginBottom: 12 }}>
                {editing.collectionGuid ? t('common.editCollection') : t('common.newCollection')}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <div className={styles.field}>
                  <div className={styles.labelRow}><span className={styles.label}>{t('collections.nameRequired')}</span></div>
                  <input className={styles.input} value={editing.collectionName} onChange={(e) => setEditing({ ...editing, collectionName: e.target.value })} placeholder="e.g. Rotary District 7930" disabled={saving} autoFocus />
                </div>
                <div className={styles.field}>
                  <div className={styles.labelRow}><span className={styles.label}>Custom Metadata Schema</span></div>
                  <select className={styles.select} value={editing.schemaGuid} onChange={(e) => setEditing({ ...editing, schemaGuid: e.target.value })} disabled={saving}>
                    <option value="">-- No Schema --</option>
                    {schemas.map((s) => (
                      <option key={s.schemaGuid} value={s.schemaGuid}>{s.schemaName} (v{s.version})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <div className={styles.field} ref={ownerOrgRef} style={{ position: "relative" }}>
                  <div className={styles.labelRow} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span className={styles.label}>Owner / Organization</span>
                    <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: "1px solid var(--border-default)" }}>
                      {(["all", "geo", "org"] as const).map((f) => (
                        <button
                          key={f}
                          type="button"
                          onClick={() => { setOwnerTypeFilter(f); if (ownerOrgQuery.trim()) setShowOwnerSuggestions(true); }}
                          style={{
                            padding: "2px 8px",
                            fontSize: 10,
                            fontWeight: 600,
                            border: "none",
                            cursor: "pointer",
                            background: ownerTypeFilter === f ? "rgba(99,102,241,0.4)" : "var(--bg-hover)",
                            color: ownerTypeFilter === f ? "#e0e7ff" : "var(--text-muted)",
                          }}
                        >
                          {f === "all" ? "All" : f === "geo" ? "Geo" : "Org"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    className={styles.input}
                    value={ownerOrgQuery}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOwnerOrgQuery(v);
                      setEditing({ ...editing, ownerOrg: v });
                      setShowOwnerSuggestions(v.trim().length > 0);
                    }}
                    onFocus={() => { if (ownerOrgQuery.trim()) setShowOwnerSuggestions(true); }}
                    placeholder={t('common.searchOrganizations')}
                    disabled={saving}
                    autoComplete="off"
                  />
                  {showOwnerSuggestions && ownerOrgSuggestions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        insetInline: 0,
                        maxHeight: 220,
                        overflowY: "auto",
                        borderRadius: 10,
                        border: "1px solid var(--border-default)",
                        background: "rgba(12,14,16,0.98)",
                        boxShadow: "0 16px 48px rgba(0,0,0,0.55)",
                        zIndex: 30,
                      }}
                    >
                      {ownerOrgSuggestions.map((ent) => (
                        <button
                          key={ent.entityGuid}
                          type="button"
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            padding: "8px 12px",
                            border: "none",
                            background: "transparent",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-active)"; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                          onClick={() => {
                            setOwnerOrgQuery(ent.name);
                            setEditing({ ...editing, ownerOrg: ent.name });
                            setShowOwnerSuggestions(false);
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{
                              display: "inline-block",
                              padding: "1px 6px",
                              borderRadius: 6,
                              fontSize: 9,
                              fontWeight: 700,
                              background: ent.entityType.startsWith("geo_") ? "var(--accent-primary)" : "var(--color-warning)",
                              color: "var(--text-primary)",
                            }}>
                              {ent.entityType.startsWith("geo_") ? "Geo" : "Org"}
                            </span>
                            <span style={{ fontWeight: 600 }}>{ent.name}</span>
                          </div>
                          <div style={{ opacity: 0.5, fontSize: 11, marginTop: 1 }}>
                            {ent.entityType.replace(/^geo_|^org_/, "").replace(/_/g, " ")}{ent.code ? ` \u00B7 ${ent.code}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {editing.collectionGuid ? (
                  <div className={styles.field} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 14 }}>
                    <label className={styles.checkboxLabel}>
                      <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} disabled={saving} />
                      Active
                    </label>
                  </div>
                ) : <div />}
              </div>
              <div className={styles.field}>
                <div className={styles.labelRow}><span className={styles.label}>Description</span></div>
                <textarea className={styles.textarea} style={{ minHeight: 60 }} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Brief description of this collection" disabled={saving} />
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                <button className={styles.btnSecondary} type="button" onClick={cancelEdit} disabled={saving}>Cancel</button>
                <button className={styles.btnPrimary} type="button" onClick={saveCollection} disabled={saving}>
                  {saving ? "Saving..." : editing.collectionGuid ? "Update" : "Create"}
                </button>
              </div>
            </div>
          ) : null}

          {/* Collection table */}
          <div className={styles.memberTableWrap}>
            <table className={styles.memberTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Custom Metadata Schema</th>
                  <th>Owner</th>
                  <th>Projects</th>
                  <th>Status</th>
                  <th style={{ width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCollections.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 24 }}>
                      {collections.length === 0 ? (
                        <div>
                          <div style={{ fontSize: 22, marginBottom: 6, opacity: 0.5 }}>{"\uD83D\uDCC1"}</div>
                          <div style={{ opacity: 0.7, fontSize: 13 }}>No collections yet. Click <b>+ New Collection</b> to create one.</div>
                        </div>
                      ) : (
                        <div style={{ opacity: 0.6, fontSize: 13 }}>No collections match your search.</div>
                      )}
                    </td>
                  </tr>
                ) : null}
                {filteredCollections.map((c) => {
                  const projCount = projectCountByCollection[c.collectionGuid] || 0;
                  const isExpanded = expandedGuid === c.collectionGuid;
                  const isPermsExpanded = permissionsExpandedGuid === c.collectionGuid;
                  const linkedProjects = isExpanded ? projectsForCollection(c.collectionGuid) : [];

                  return (
                    <React.Fragment key={c.collectionGuid}>
                      <tr style={{ opacity: c.isActive ? 1 : 0.65, transition: "opacity 150ms ease" }}>
                        <td>
                          <div className={styles.memberNameRow} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {/* Pencil icon for edit */}
                            <button
                              type="button"
                              onClick={() => startEdit(c)}
                              disabled={saving || !!editing}
                              title={`Edit "${c.collectionName}"`}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: saving || editing ? "not-allowed" : "pointer",
                                padding: 2,
                                opacity: saving || editing ? 0.35 : 0.7,
                                transition: "opacity 120ms ease",
                                flexShrink: 0,
                                lineHeight: 1,
                              }}
                              onMouseEnter={(e) => { if (!saving && !editing) (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = saving || editing ? "0.35" : "0.7"; }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(226,232,240,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                              </svg>
                            </button>
                            <span className={styles.memberName}>{c.collectionName}</span>
                          </div>
                          {c.description ? <div className={styles.memberEmail}>{c.description}</div> : null}
                          <div className={styles.memberGuid}>{c.collectionGuid}</div>
                        </td>
                        <td><span className={styles.pillSoft}>{schemaName(c.schemaGuid)}</span></td>
                        <td><span style={{ fontSize: 12, color: "rgba(226,232,240,0.85)" }}>{c.ownerOrg || <span style={{ opacity: 0.45 }}>—</span>}</span></td>
                        <td>
                          {projCount > 0 ? (
                            <button className={styles.btnMini} type="button" onClick={() => setExpandedGuid(isExpanded ? null : c.collectionGuid)} title={isExpanded ? "Collapse" : "Expand"}>
                              {projCount} project{projCount !== 1 ? "s" : ""} {isExpanded ? "\u25B2" : "\u25BC"}
                            </button>
                          ) : (
                            <span style={{ opacity: 0.45, fontSize: 12 }}>0 projects</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={c.isActive ? styles.pill : styles.pillSoft}
                            style={c.isActive ? { borderColor: "rgba(34,197,94,0.40)", background: "rgba(34,197,94,0.12)", color: "rgba(240,253,244,0.92)" } : {}}
                          >
                            {c.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              className={styles.btnMini}
                              type="button"
                              onClick={() => setPermissionsExpandedGuid(isPermsExpanded ? null : c.collectionGuid)}
                              title="Manage who can access this collection and its projects"
                              style={isPermsExpanded ? {
                                borderColor: "rgba(99,102,241,0.55)",
                                background: "rgba(99,102,241,0.15)",
                                color: "rgba(224,231,255,0.95)",
                              } : {}}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "-1px", marginInlineEnd: 4 }}>
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0110 0v4" />
                              </svg>
                              Permissions{(() => { const mc = membershipsForCollection(c.collectionGuid).length; return mc > 0 ? ` (${mc})` : ""; })()}
                            </button>
                            <button className={styles.btnMini} type="button" onClick={() => confirmDeactivate(c)} disabled={saving} title={c.isActive ? "Deactivate" : "Activate"}>
                              {c.isActive ? "Deactivate" : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Linked projects expand */}
                      {isExpanded && !isPermsExpanded && linkedProjects.length > 0 ? (
                        <tr>
                          <td colSpan={6} style={{ padding: "0 12px 12px 24px", background: "rgba(2,6,23,0.25)" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, marginTop: 8, color: "rgba(226,232,240,0.82)" }}>
                              Linked Projects ({linkedProjects.length})
                            </div>
                            {linkedProjects.map((cp) => {
                              // DEV-1096: projectsForCollection pre-filters to project rows
                              // where projectGuid is guaranteed non-null. The bang is a
                              // TS-level reflection of that runtime invariant.
                              const pg = cp.projectGuid!;
                              return (
                              <div key={pg} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12 }}>
                                <span style={{ fontWeight: 600, color: "rgba(248,250,252,0.9)" }}>{projectName(pg)}</span>
                                <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", opacity: 0.5, fontSize: 11 }}>{pg}</span>
                                <button className={styles.btnMini} type="button" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => confirmUnlink(c.collectionGuid, pg, c.collectionName)} disabled={saving} title="Unlink">
                                  Unlink
                                </button>
                              </div>
                              );
                            })}
                          </td>
                        </tr>
                      ) : null}

                      {/* Permissions panel — collection membership management */}
                      {isPermsExpanded ? (
                        <tr>
                          <td colSpan={6} style={{ padding: "0 12px 16px 12px", background: "rgba(2,6,23,0.25)" }}>
                            <div style={{ marginTop: 10, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0110 0v4" />
                              </svg>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(224,231,255,0.92)" }}>
                                Collection Permissions — {c.collectionName}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: "rgba(226,232,240,0.55)", marginBottom: 10, lineHeight: 1.4 }}>
                              Grant users access to this collection and its projects. Viewers can see projects; Contributors can also edit project content.
                            </div>

                            {/* Add member controls */}
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, position: "relative" }}>
                              <div style={{ flex: 1, position: "relative" }}>
                                <input
                                  className={styles.input}
                                  style={{ width: "100%", padding: "6px 10px", fontSize: 12 }}
                                  placeholder="Search users to add..."
                                  value={memberSearch}
                                  onChange={(e) => {
                                    setMemberSearch(e.target.value);
                                    if (selectedUserGuid) setSelectedUserGuid("");
                                  }}
                                  onFocus={() => { if (memberSearch.trim()) setMemberSearch(memberSearch); }}
                                />
                                {memberSearch.trim() && !selectedUserGuid && memberSearchResults.length > 0 ? (
                                  <div style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    insetInline: 0,
                                    maxHeight: 180,
                                    overflowY: "auto",
                                    borderRadius: 10,
                                    border: "1px solid var(--border-default)",
                                    background: "rgba(12,14,16,0.98)",
                                    boxShadow: "0 16px 48px rgba(0,0,0,0.55)",
                                    zIndex: 30,
                                  }}>
                                    {memberSearchResults.map((u) => (
                                      <button
                                        key={u.userGuid}
                                        type="button"
                                        style={{
                                          display: "block",
                                          width: "100%",
                                          textAlign: "left",
                                          padding: "8px 12px",
                                          border: "none",
                                          background: "transparent",
                                          color: "var(--text-primary)",
                                          cursor: "pointer",
                                          fontSize: 12,
                                        }}
                                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-active)"; }}
                                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                                        onMouseDown={() => {
                                          setSelectedUserGuid(u.userGuid);
                                          setMemberSearch(u.displayName);
                                        }}
                                      >
                                        <span style={{ fontWeight: 600 }}>{u.displayName}</span>
                                        {u.isSystemAdmin ? (
                                          <span style={{ fontSize: 10, marginInlineStart: 6, padding: "1px 5px", borderRadius: 4, background: "rgba(251,191,36,0.2)", color: "rgba(254,243,199,0.9)" }}>admin</span>
                                        ) : null}
                                      </button>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                              <select
                                className={styles.select}
                                style={{ width: 130, padding: "6px 10px", fontSize: 12 }}
                                value={selectedMemberRole}
                                onChange={(e) => setSelectedMemberRole(e.target.value)}
                              >
                                <option value="member_viewer">{roleLabel("member_viewer")}</option>
                                <option value="member_contributor">{roleLabel("member_contributor")}</option>
                              </select>
                              <button
                                className={styles.btnPrimary}
                                style={{ padding: "6px 14px", fontSize: 12, flexShrink: 0 }}
                                type="button"
                                disabled={!selectedUserGuid || saving}
                                onClick={() => {
                                  if (selectedUserGuid) {
                                    handleAddMember(c.collectionGuid, selectedUserGuid, selectedMemberRole);
                                  }
                                }}
                              >
                                Add
                              </button>
                            </div>

                            {/* Current members list */}
                            {(() => {
                              const members = membershipsForCollection(c.collectionGuid);
                              if (members.length === 0) {
                                return (
                                  <div style={{ fontSize: 12, color: "rgba(226,232,240,0.45)", padding: "12px 0" }}>
                                    No members yet. Use the search above to add users.
                                  </div>
                                );
                              }
                              return (
                                <div style={{
                                  borderRadius: 10,
                                  border: "1px solid var(--border-subtle)",
                                  overflow: "hidden",
                                }}>
                                  {/* Header */}
                                  <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 130px 60px",
                                    padding: "8px 12px",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "rgba(226,232,240,0.6)",
                                    background: "var(--bg-hover)",
                                    borderBottom: "1px solid var(--bg-hover-strong)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                  }}>
                                    <div>User</div>
                                    <div>Role</div>
                                    <div></div>
                                  </div>
                                  {/* Member rows */}
                                  {members.map((m, idx) => {
                                    const mKey = `${c.collectionGuid}|${m.userGuid}`;
                                    const isSavingThis = savingMemberKey === mKey;
                                    const isLast = idx === members.length - 1;

                                    return (
                                      <div
                                        key={m.id}
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns: "1fr 130px 60px",
                                          alignItems: "center",
                                          padding: "8px 12px",
                                          borderBottom: isLast ? "none" : "1px solid var(--bg-hover)",
                                          fontSize: 12,
                                        }}
                                      >
                                        <div>
                                          <span style={{ fontWeight: 600, color: "rgba(248,250,252,0.9)" }}>
                                            {userName(m.userGuid)}
                                          </span>
                                          {isSavingThis ? (
                                            <span style={{ fontSize: 10, color: "rgba(226,232,240,0.5)", marginInlineStart: 6 }}>saving...</span>
                                          ) : null}
                                        </div>
                                        <div>
                                          <select
                                            className={styles.select}
                                            style={{ width: "100%", padding: "4px 8px", fontSize: 12 }}
                                            value={m.role}
                                            onChange={(e) => handleRoleChange(c.collectionGuid, m.userGuid, e.target.value)}
                                            disabled={isSavingThis || saving}
                                          >
                                            <option value="member_viewer">{roleLabel("member_viewer")}</option>
                                            <option value="member_contributor">{roleLabel("member_contributor")}</option>
                                            <option value="collection_admin">{roleLabel("collection_admin")}</option>
                                          </select>
                                        </div>
                                        <div style={{ textAlign: "center" }}>
                                          <button
                                            className={styles.btnMini}
                                            type="button"
                                            style={{ padding: "2px 8px", fontSize: 11 }}
                                            onClick={() => confirmRemoveMember(m, c.collectionName)}
                                            disabled={saving}
                                            title="Remove member"
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.mutedTiny} style={{ marginTop: 10 }}>
            Collections group projects and attach custom metadata schemas. Use the "Assign Projects" tab to assign projects to collections.
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── PROJECTS SUB-TAB (collection-centric checklist) ──────── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {subTab === "projects" && (
        <>
          {/* Toolbar: collection picker + search */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <div className={styles.sectionTitle} style={{ marginBottom: 0, flexShrink: 0 }}>
              Assign Projects
            </div>
            <div style={{ flex: 1 }} />
            <select
              className={styles.select}
              style={{ width: 240, padding: "6px 10px", fontSize: 12 }}
              value={assignCollGuid}
              onChange={(e) => setAssignCollGuid(e.target.value)}
            >
              <option value="">-- Select a Collection --</option>
              {activeCollections.map((c) => (
                <option key={c.collectionGuid} value={c.collectionGuid}>{c.collectionName}</option>
              ))}
            </select>
            <input
              className={styles.input}
              style={{ width: 200, padding: "6px 10px", fontSize: 12 }}
              placeholder="Search projects..."
              value={projSearch}
              onChange={(e) => setProjSearch(e.target.value)}
            />
          </div>

          {activeCollections.length === 0 ? (
            <div className={styles.notice}>
              No active collections. Create a collection first on the "Manage Collections" tab.
            </div>
          ) : !assignCollGuid ? (
            <div className={styles.placeholder}>
              Select a collection above to manage its project membership.
            </div>
          ) : (
            <>
              {/* DEV-1096. Toggle between Full-Project and Mini-Project pickers.
                  Skip's locked decision: a toggle, not two separate sub-tabs and
                  not a single merged list. Buttons inherit the same .btnMini
                  affordance as the surrounding tab and indicate selection
                  through the visited-button color treatment. */}
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                <button
                  type="button"
                  className={styles.btnMini}
                  style={pickerMode === "projects"
                    ? { background: "rgba(59,130,246,0.18)", borderColor: "rgba(59,130,246,0.55)", color: "rgba(248,250,252,0.95)" }
                    : {}}
                  onClick={() => setPickerMode("projects")}
                >
                  Projects
                </button>
                <button
                  type="button"
                  className={styles.btnMini}
                  style={pickerMode === "mini-projects"
                    ? { background: "rgba(59,130,246,0.18)", borderColor: "rgba(59,130,246,0.55)", color: "rgba(248,250,252,0.95)" }
                    : {}}
                  onClick={() => setPickerMode("mini-projects")}
                >
                  Mini-Projects
                </button>
              </div>

              {pickerMode === "projects" ? (
                <>
                  {/* Count summary */}
                  {(() => {
                    const linkedCount = filteredProjects.filter((p) => isLinked(assignCollGuid, p.id)).length;
                    return (
                      <div style={{ fontSize: 12, color: "rgba(226,232,240,0.7)", marginBottom: 10 }}>
                        {linkedCount} of {filteredProjects.length} project{filteredProjects.length !== 1 ? "s" : ""} linked
                      </div>
                    );
                  })()}

                  <div className={styles.memberTableWrap}>
                    <table className={styles.memberTable}>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>Project Name</th>
                          <th>Status</th>
                          <th>Start Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProjects.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                              {projects.length === 0 ? (
                                <div style={{ opacity: 0.6, fontSize: 13 }}>
                                  No projects in this community. Import projects via the Imports tab.
                                </div>
                              ) : eligibleLoading ? (
                                <div style={{ opacity: 0.6, fontSize: 13 }}>Checking eligibility…</div>
                              ) : eligibleGuids && eligibleGuids.size === 0 ? (
                                <div style={{ opacity: 0.7, fontSize: 13 }}>
                                  No eligible projects. Projects must have a primary owner
                                  (or active partner organization) matching this collection's
                                  owner organization. Re-parent a project, add a partner,
                                  or change the collection's owner scope.
                                </div>
                              ) : (
                                <div style={{ opacity: 0.6, fontSize: 13 }}>No projects match your search.</div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                        {filteredProjects.map((p) => {
                          const linked = isLinked(assignCollGuid, p.id);
                          const isSavingThis = savingProjectId === p.id;
                          return (
                            <tr
                              key={p.id}
                              style={{
                                cursor: isSavingThis ? "wait" : "pointer",
                                background: linked ? "rgba(59,130,246,0.06)" : "transparent",
                                transition: "background 120ms ease",
                              }}
                              onClick={() => { if (!isSavingThis) toggleProjectInCollection(p.id); }}
                            >
                              <td style={{ textAlign: "center", width: 40 }}>
                                <input
                                  type="checkbox"
                                  checked={linked}
                                  disabled={isSavingThis}
                                  onChange={() => {}}
                                  style={{ width: 16, height: 16, cursor: isSavingThis ? "wait" : "pointer" }}
                                />
                              </td>
                              <td>
                                <div className={styles.memberNameRow}>
                                  <span className={styles.memberName}>{p.name}</span>
                                  {isSavingThis ? (
                                    <span style={{ fontSize: 11, color: "rgba(226,232,240,0.5)" }}>saving...</span>
                                  ) : null}
                                </div>
                                <div className={styles.memberGuid}>{p.id}</div>
                              </td>
                              <td>
                                <span className={styles.pillSoft}>{p.projectStatus || miniLifecycleToProjectStatus(p.status) || p.status || "—"}</span>
                              </td>
                              <td>
                                <span style={{ fontSize: 12, color: "rgba(226,232,240,0.85)" }}>
                                  {p.startDate || <span style={{ opacity: 0.45 }}>—</span>}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                // ── DEV-1096. Mini-Project picker ─────────────────────────
                //
                // The eligible list is server-filtered to:
                //   • visibility public/community in the collection's owner-community,
                //     OR
                //   • the caller is on the MP's team / role roster (regardless of vis).
                // `viaMembership` on each row flags the second case so the UI can
                // explain why a team_only MP appears.
                <>
                  {(() => {
                    const rows = eligibleMiniProjects || [];
                    const linkedCount = rows.filter((mp) => isMpLinked(assignCollGuid, mp.miniProjectGuid)).length;
                    return (
                      <div style={{ fontSize: 12, color: "rgba(226,232,240,0.7)", marginBottom: 10 }}>
                        {linkedCount} of {rows.length} Mini-Project{rows.length !== 1 ? "s" : ""} linked
                      </div>
                    );
                  })()}

                  <div className={styles.memberTableWrap}>
                    <table className={styles.memberTable}>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>Mini-Project Title</th>
                          <th>Visibility</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleMpLoading ? (
                          <tr>
                            <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                              <div style={{ opacity: 0.6, fontSize: 13 }}>Loading eligible Mini-Projects…</div>
                            </td>
                          </tr>
                        ) : (eligibleMiniProjects || []).length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ textAlign: "center", padding: 24 }}>
                              <div style={{ opacity: 0.7, fontSize: 13 }}>
                                No eligible Mini-Projects. Mini-Projects appear when
                                their owner community matches this collection (and the
                                visibility allows the caller to see them, or the caller
                                is on the team / role roster).
                              </div>
                            </td>
                          </tr>
                        ) : (
                          (eligibleMiniProjects || []).map((mp) => {
                            const linked = isMpLinked(assignCollGuid, mp.miniProjectGuid);
                            const isSavingThis = savingMiniProjectId === mp.miniProjectGuid;
                            return (
                              <tr
                                key={mp.miniProjectGuid}
                                style={{
                                  cursor: isSavingThis ? "wait" : "pointer",
                                  background: linked ? "rgba(59,130,246,0.06)" : "transparent",
                                  transition: "background 120ms ease",
                                }}
                                onClick={() => { if (!isSavingThis) toggleMiniProjectInCollection(mp.miniProjectGuid); }}
                              >
                                <td style={{ textAlign: "center", width: 40 }}>
                                  <input
                                    type="checkbox"
                                    checked={linked}
                                    disabled={isSavingThis}
                                    onChange={() => {}}
                                    style={{ width: 16, height: 16, cursor: isSavingThis ? "wait" : "pointer" }}
                                  />
                                </td>
                                <td>
                                  <div className={styles.memberNameRow}>
                                    <span className={styles.memberName}>{mp.title}</span>
                                    {mp.viaMembership ? (
                                      <span
                                        title="Visible to you because you're on the Mini-Project's team or hold an active role on it."
                                        style={{ fontSize: 10, padding: "1px 6px", borderRadius: 6, background: "rgba(59,130,246,0.15)", color: "rgba(191,219,254,0.95)" }}
                                      >
                                        via membership
                                      </span>
                                    ) : null}
                                    {isSavingThis ? (
                                      <span style={{ fontSize: 11, color: "rgba(226,232,240,0.5)" }}>saving...</span>
                                    ) : null}
                                  </div>
                                  <div className={styles.memberGuid}>{mp.miniProjectGuid}</div>
                                </td>
                                <td>
                                  <span className={styles.pillSoft}>{mp.visibility}</span>
                                </td>
                                <td>
                                  <span style={{ fontSize: 12, color: "rgba(226,232,240,0.85)" }}>{mp.status}</span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          <div className={styles.mutedTiny} style={{ marginTop: 10 }}>
            Select a collection, then check or uncheck projects to add or remove them. A project can belong to multiple collections.
          </div>
        </>
      )}
    </div>
  );
};

export default CommunityCollectionsTab;
